/**
 * Global error handler interceptor for all axios responses
 */

import { flatMap, uniq } from 'lodash-es'
import { useAuthStore } from 'src/stores/authStore.js'
import { showErrorDialog as showDefaultErrorDialog } from './dialogs.js'
import { appUrl } from 'src/consts.js'
import { getActiveLanguage, setActiveLanguage } from './i18n.js'

// We'll replace these laravel defaults with a more-friendlier version (since there doesnt seem to be any way to change these from within laravel)
const LARAVEL_DEFAULT_403 = 'This action is unauthorized.'
const LARAVEL_DEFAULT_500 = 'Server Error'

let isHandlingError = false // Locking var to prevent the handler being called multiple times in quick succession

export default async function axiosErrorHandler(error, rethrow = true) {
    if (!getActiveLanguage()) await setActiveLanguage() // Ensure i18n is loaded first. May not be if user is unauthed

    // Error come from something other than axios (somehow) or user opted out of default handler? Just bail
    if (!error.isAxiosError || error.config?.disableDefaultHandler) throw error

    // No need to call repeadedly on multiple http requests that fail in quick succession
    if (!isHandlingError) {
        isHandlingError = true
        // General network error with no response data?
        if (!error || !error.response) {
            showErrorDialog()
        } else {
            handleHttpStatusCodeError(error)
        }
    }

    if (rethrow) throw error // re-throw to cancel any pending promise chains
}

/**
  * Show a friendly message to the end user based on the http status code received
  */
function handleHttpStatusCodeError(error) {
    const { status, data } = error.response

    switch (status) {
        case 0: return // Do nothing on XHR aborts
        case 401: return handleUnauthenticatedError()
        case 403: return showErrorDialog(
            data.message && data.message != LARAVEL_DEFAULT_403 ? data.message : 'errors_forbidden_message',
            'errors_forbidden_title',
        )
        case 404: return showErrorDialog('errors_not_found_message', 'errors_not_found_title')
        case 413: return showErrorDialog('errors_upload_too_large_message', 'errors_upload_too_large_title')
        case 422:
            // Also handles laravel validation messages
            return showErrorDialog(
                uniq(flatMap(data.errors, messages => messages)).join('<br>') ||
                      data.message || 'errors_validation_message',
            )
        case 500:
            // No specific response returned / internal server error? Just show general error message.
            // When debugging is false we laravel just gives us "Server Error" which is a bit unfriendly.
            return showErrorDialog(
                data.message !== LARAVEL_DEFAULT_500 ? data.message : 'errors_server_error_message',
                'errors_server_error_title',
            )
        case 502:
        case 503:
        case 504:
            return showErrorDialog(
                'errors_server_unavailable_message',
                'errors_server_unavailable_title',
            )
        default:
            return showErrorDialog()
    }
}

function showErrorDialog(message, title) {
    return showDefaultErrorDialog(
        trans(message || 'errors_unable_to_communicate_with_server'),
        trans(title || 'errors_whoops'),
    )
        .onDismiss(() => isHandlingError = false)
}

/**
  * We can handle any 401 errors by redirecting the user back to the login page
  */
async function handleUnauthenticatedError() {
    useAuthStore().logout()

    let dialog = await showErrorDialog('errors_unauthenticated_message', 'errors_unauthenticated_title')

    // For this app - triggering a full page reload should suffice as auth happens  automatically by the laravel sanctum auth guard
    // i.e User should already be authed on the backend before the SPA is even booted
    dialog.onOk(() => location.href = `${appUrl}/auth/login`)
}

